/**
 * Attention.tsx · 09_APP · OS-006
 *
 * Everything waiting on a human, from os_attention.
 *
 * THE HARD CAP OF SEVEN, with a NAMED overflow. Emily's correction, applied to
 * the prototype on 2026-08-02: seven items, and what is left over is named
 * rather than hidden behind "and 12 more". A count you cannot inspect is a
 * count you learn to ignore.
 *
 * An unconfirmed proposed need is COUNTED here and never acted on. That is the
 * whole point of person_need_active, and it is why this screen shows numbers
 * for those rather than offering a button.
 */

import { useState } from "react";
import type { AttentionPayload } from "../lib/os";
import { MatchReview } from "./MatchReview";

const CAP = 7;

interface Item {
  key: string;
  what: string;
  detail: string;
}

export function toItems(a: AttentionPayload): Item[] {
  const items: Item[] = [];

  for (const p of a.pending_proposals ?? []) {
    items.push({
      key: `proposal:${p.id}`,
      what: `A change to ${p.table} is waiting for you`,
      detail: p.why,
    });
  }

  for (const c of a.credentials ?? []) {
    items.push({
      key: `credential:${c.who}:${c.what}`,
      what: `${c.what} for ${c.who} is ${c.state}`,
      detail: c.expires ? `Expires ${c.expires}` : "No expiry recorded",
    });
  }

  if (a.stale_referrals > 0) {
    items.push({
      key: "stale_referrals",
      what: `${a.stale_referrals} referral${a.stale_referrals === 1 ? "" : "s"} with no movement for three weeks`,
      detail: "Open, and nothing has changed on them since.",
    });
  }

  if (a.review_queue > 0) {
    items.push({
      key: "review_queue",
      what: `${a.review_queue} possible match${a.review_queue === 1 ? "" : "es"} to check`,
      detail: "Possible connections, verify availability. Not recommendations.",
    });
  }

  if (a.unconfirmed_needs > 0) {
    items.push({
      key: "unconfirmed_needs",
      what: `${a.unconfirmed_needs} proposed need${a.unconfirmed_needs === 1 ? "" : "s"} nobody has confirmed`,
      detail: "Counted, not acted on. Each carries the sentence it came from.",
    });
  }

  return items;
}

export function Attention({ data: initial }: { data: AttentionPayload }) {
  // 2026-10-20, her words: "you can't click on anything on that page." Each item now opens what it is about.
  const [data, setData] = useState(initial);
  const [open, setOpen] = useState<string | null>(null);
  const items = toItems(data);
  const shown = items.slice(0, CAP);
  const overflow = items.slice(CAP);

  if (items.length === 0) {
    return (
      <div className="card">
        <header>
          <h3>Waiting on you</h3>
          <span className="hint">nothing right now</span>
        </header>
        <div className="pad muted">
          Nothing is waiting. That is a real state, not an empty screen.
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <header>
        <h3>Waiting on you</h3>
        <span className="hint">
          {items.length > CAP ? `${CAP} of ${items.length}` : `${items.length}`}
        </span>
      </header>
      <div className="pad">
        {shown.map((i) => (
          <div key={i.key} style={{ marginBottom: 12 }}>
            <button className="att-item" onClick={() => setOpen(open === i.key ? null : i.key)} aria-expanded={open === i.key}>
              <span style={{ fontWeight: 600 }}>{i.what}</span>
              <span className="muted" style={{ fontSize: 13 }}>{i.detail}</span>
              <span className="att-go">{open === i.key ? "Close ▴" : "Open ▾"}</span>
            </button>
            {open === i.key && (i.key === "review_queue"
              ? <MatchReview onDone={(left) => setData({ ...data, review_queue: left })} />
              : <p className="muted" style={{ fontSize: 13, margin: "6px 2px" }}>Ask Claude about this one with the bar at the top; this kind does not have its own screen yet.</p>)}
          </div>
        ))}

        {overflow.length > 0 && (
          <div className="overflow">
            Also waiting, and named rather than counted:{" "}
            {overflow.map((i, n) => (
              <span key={i.key}>
                {n > 0 ? "; " : ""}
                <b>{i.what}</b>
              </span>
            ))}
            .
          </div>
        )}
      </div>
    </div>
  );
}
